import {UserBasic, UserWithCareProgram} from "@shared/api/models/query/user.model";
import {DepartmentBasic} from "@shared/api/models/query/department.model";

/**
 * Mirrors the backend PatientStatusEnum (api.cloud.univa.health).
 */
export enum PatientStatusEnum {
    DRAFT = 'draft',
    ONBOARDING = 'onboarding',
    ACTIVE = 'active',
    COMPLETED = 'completed',
}

/**
 * UNIVAPLAT-3719 (child mode): the child's details come back on the same keys they are saved
 * with (`user.firstName`, `dateOfBirth`, `age`), so the registration summary reads them from
 * here; no separate child fields exist on the API.
 */
export interface PatientBasic {
    id: number,
    user: UserWithCareProgram,
    carer: Carer,
    hasCarer: boolean,
    nhsNumber: string,
    dateOfBirth: string,
    age: number,
    biologicalSex: string,
    weight: number,
    height: number,
    neckCircumference: number,
    bmi: number,
    bmiMedianPercentage: number,
    department: DepartmentBasic,
    wizardStep: number,
    status: string,
    type?: string,
    // UNIVAPLAT-2827: active clinician assignment (backend ClinicianPatientResponse).
    // Only present when the patient was registered by an administrator; `user` is the
    // clinician's user record.
    clinician?: ClinicianPatientAssignment,
    diagnosis: string,
    adherence: {
        checkins: {
            scheduled: number,
            completed: number,
            percentage: number,
        }
    }
    promsTrend: {
        previousScore: number,
        lastScore: number,
        trend: string,
    }

    modulesAdherenceLastWeekPercentage: number,
    checkinsAdherenceLastWeekPercentage: number,
    foodDiaryAdherenceLastWeekPercentage: number,
    meedRisk?: {
        status: 'Low' | 'Moderate' | 'High' | 'Unknown',
        trend: 'Deteriorating' | 'Stable' | 'Improving' | 'Unknown'
    }
}

export interface ClinicianPatientAssignment {
    id: number,
    user: UserBasic,
    createdAt: string,
    createdBy: UserBasic,
}

export interface Patient extends PatientBasic {
    carerRelationship: CarerRelationship,
    carerRelationshipNotes: string,
    carer: Carer,
    nextAppointmentNumber: number,
}

export interface PatientStatistics {
    modulesAdherence: WeekValue[],
    checkinsAdherence: WeekValue[],
    foodDiaryAdherence: WeekValue[],
}

export interface PatientNote {
    id: number,
    note: string,
    createdBy: {
        id: number,
        firstName: string,
        lastName: string,
    }
    createdAt: string,
    updatedAt: string,
}

export interface WeekValue {
    week: number,
    value: number,
}

export interface Carer {
    id: number,
    user: UserWithCareProgram,
    dateOfBirth: string,
}

export interface CarerRelationship {
    id: number,
    name: string,
}
